/**
 * CustomerOverview — the customer window's OVERVIEW tab, laid out like FieldRoutes'
 * Info tab: two columns.
 *
 *   left  — what the office edits: contact, address, preferences, source, property
 *           type, flags. Inline fields (label 4px above value) saved by the window's
 *           Save, which sends only the fields that changed (D-036, customerPatch.js).
 *   right — the account as it stands (plan, frequency, price, last/next service,
 *           tech, rep, autopay, balance, lifetime paid, customer since) and a SMALL
 *           map (240px) with an expand button. Never a full-width map.
 *
 * Under the columns: the Account brief (collapsed unless it has content), then Plans
 * and Appointments as compact lists. Each block has its own error boundary.
 *
 * Everything reads record.raw — the customer detail payload. `raw.fieldroutes` is
 * the FieldRoutes summary resolved through the mirror link. Unknown is never blank
 * or 0: a fact we don't have renders "—" with the reason.
 */
import React, { useMemo } from "react";
import AccountBriefCard from "../Customers/AccountBrief/AccountBriefCard";
import AccountBriefBoundary from "../Customers/AccountBrief/AccountBriefBoundary";
import CardBoundary from "../ui/CardBoundary";
import PropertyMap, { resolveKnownCoords } from "../maps/PropertyMap";
import Badge from "../ui/badge";
import { Input, Select, Textarea, Field } from "../ui/input";
import { Checkbox } from "../ui/Toggle";
import {
  clean,
  money,
  shortDate,
  plainWords,
  frequencyWords,
  coverageText,
} from "./customerFormat";
import "./CustomerOverview.css";

function buildAddress(raw) {
  const street = clean(raw.address);
  const locality = [clean(raw.city), clean(raw.state), clean(raw.zip_code)]
    .filter(Boolean)
    .join(" ");
  return [street, locality].filter(Boolean).join(", ");
}

const PLAN_ACTIVE = new Set(["active", "current", "enrolled", "open"]);

// Customer.preferred_channel values the reminder path reads (utils/reminders.py).
const CHANNELS = [
  { value: "sms", label: "Text" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Call" },
  { value: "both", label: "Text and email" },
];

const NOT_LINKED = "Not linked to FieldRoutes";

/* One read-only fact: the value, or "—" with why we don't have it. */
function Fact({ label, value, reason, tone, num }) {
  const known = value != null && value !== "";
  return (
    <div className="cov-fact">
      <span className="cov-fact__label">{label}</span>
      {known ? (
        <span className={`cov-fact__val${num ? " num" : ""}${tone ? ` cov-fact__val--${tone}` : ""}`}>{value}</span>
      ) : (
        <span className="cov-fact__val cov-fact__val--unknown">
          —{reason ? <span className="cov-fact__why">{reason}</span> : null}
        </span>
      )}
    </div>
  );
}

export default function CustomerOverview({ record, formData = {}, onChange }) {
  const raw = useMemo(() => record?.raw || {}, [record]);
  const fr = raw.fieldroutes || null;
  // The pin: coordinates on the record (RT's leg) → FieldRoutes → geocode the address.
  const knownCoords = useMemo(() => resolveKnownCoords(raw, fr), [raw, fr]);
  // the CRM customer id (the brief is keyed on it); FR-only records have none
  const briefCustomerId =
    raw.object === "customer" || !raw.object ? raw.id || raw.customer_id || null : raw.customer_id || null;

  const plans = Array.isArray(raw.service_plans) ? raw.service_plans : [];
  const crmAppointments = Array.isArray(raw.appointments) ? raw.appointments : [];
  const addressQuery = useMemo(() => buildAddress(raw), [raw]);
  const activePlans = plans.filter((p) => PLAN_ACTIVE.has(clean(p.status).toLowerCase()));
  const plan = activePlans[0] || null;

  // ── inline fields: the working copy (formData) over the loaded record ──
  const val = (name) => {
    const v = name in formData ? formData[name] : raw[name];
    return v == null ? "" : v;
  };
  const set = (name, value) => onChange?.({ target: { name, value } });
  const text = (label, name, opts = {}) => (
    <Field label={label} className={opts.wide ? "cov-span2" : undefined}>
      {(id) => (
        <Input
          id={id}
          type={opts.type || "text"}
          className="cov-inline"
          value={val(name)}
          placeholder={opts.placeholder || "—"}
          onChange={(e) => set(name, e.target.value)}
        />
      )}
    </Field>
  );

  // ── right column: the account ──
  const now = new Date();
  const crmNext = crmAppointments
    .filter((a) => a.start_time && new Date(a.start_time) >= now && clean(a.status).toLowerCase() !== "canceled")
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))[0];
  const planNext = plans
    .map((p) => p.next_service)
    .filter((d) => d && new Date(d) >= new Date(now.toDateString()))
    .sort()[0];
  const nextService = fr?.next_service
    ? `${shortDate(fr.next_service.date)}${fr.next_service.service ? ` · ${fr.next_service.service}` : ""}`
    : crmNext
    ? shortDate(crmNext.start_time)
    : planNext
    ? `${shortDate(planNext)} (plan)`
    : "";
  const lastService = fr?.last_service
    ? `${shortDate(fr.last_service.date)}${fr.last_service.service ? ` · ${fr.last_service.service}` : ""}`
    : raw.last_service_date
    ? shortDate(raw.last_service_date)
    : "";
  const planPrice = plan ? Number(plan.recurring_charge || plan.price) : 0;
  const payments = fr?.payments;
  const lifetime = payments
    ? payments.state === "not_synced" || payments.state === "unknown"
      ? ""
      : payments.state === "none"
      ? "No payments yet"
      : `${money(payments.total)}${payments.state === "partial" ? ` (${coverageText(payments)})` : ""}`
    : raw.revenue_source === "invoices" && Number(raw.invoice_count)
    ? money(raw.total_revenue)
    : "";
  const lifetimeWhy = payments ? coverageText(payments) : "No payments or invoices on record";
  const balanceNum = fr ? Number(fr.balance) : Number(raw.outstanding_balance);
  const balance = fr || raw.revenue_source ? money(balanceNum, { cents: true }) : "";
  const since = fr?.customer_since
    ? shortDate(fr.customer_since, true)
    : raw.created_at
    ? `${shortDate(raw.created_at, true)} (added to Abon)`
    : "";

  const acquisition = clean(raw.acquisition_source);

  return (
    <div className="cust-overview">
      <div className="cov-cols">
        {/* ── left: what the office edits ── */}
        <CardBoundary name="Details">
          <section className="cov-col" aria-label="Customer details">
            <h3 className="cov-h">Contact</h3>
            <div className="cov-grid">
              {text("First name", "first_name")}
              {text("Last name", "last_name")}
              {text("Company", "company_name", { wide: true })}
              {text("Phone", "primary_phone", { type: "tel" })}
              {text("Email", "primary_email", { type: "email" })}
              {text("Second phone", "secondary_phone", { type: "tel" })}
              {text("Second email", "secondary_email", { type: "email" })}
            </div>

            <h3 className="cov-h">Address</h3>
            <div className="cov-grid">
              {text("Street", "address", { wide: true })}
              {text("City", "city")}
              {text("State", "state")}
              {text("Zip", "zip_code")}
              {text("County", "county")}
            </div>

            <h3 className="cov-h">Preferences</h3>
            <div className="cov-grid">
              <Field label="Reach them by">
                {(id) => (
                  <Select
                    id={id}
                    className="cov-inline"
                    value={val("preferred_channel")}
                    onChange={(e) => set("preferred_channel", e.target.value)}
                  >
                    <option value="">Not set</option>
                    {CHANNELS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <div className="ds-field">
                <span className="ds-field__label">Contact allowed</span>
                {raw.do_not_contact ? (
                  <span className="cov-static">
                    <Badge tone="danger">Do not contact</Badge>
                  </span>
                ) : (
                  <span className="cov-static">Yes — texts, email and calls</span>
                )}
              </div>
            </div>

            <h3 className="cov-h">Source and property</h3>
            <div className="cov-grid">
              {text("Lead source", "lead_source")}
              {acquisition ? (
                <div className="ds-field">
                  <span className="ds-field__label">Acquired by</span>
                  <span className="cov-static">{plainWords(acquisition)}</span>
                </div>
              ) : (
                text("Property type", "property_type")
              )}
              {acquisition ? text("Property type", "property_type") : null}
              {text("Gate code", "gate_code")}
            </div>

            <h3 className="cov-h">Flags</h3>
            <div className="cov-flags">
              <Checkbox
                label="Pets on property"
                checked={!!val("has_pets")}
                onChange={(on) => set("has_pets", on)}
              />
              <Checkbox
                label="Seasonal resident"
                checked={!!val("seasonal_resident")}
                onChange={(on) => set("seasonal_resident", on)}
              />
            </div>
            <div className="cov-grid">
              <Field label="Access notes" className="cov-span2">
                {(id) => (
                  <Textarea
                    id={id}
                    className="cov-inline"
                    rows={2}
                    value={val("access_notes")}
                    placeholder="Gate, dogs, where to park — what a tech should know"
                    onChange={(e) => set("access_notes", e.target.value)}
                  />
                )}
              </Field>
            </div>
          </section>
        </CardBoundary>

        {/* ── right: the account ── */}
        <CardBoundary name="Account">
          <section className="cov-col" aria-label="Account">
            <h3 className="cov-h">Account</h3>
            <div className="cov-facts">
              <Fact
                label="Plan"
                value={plan ? clean(plan.plan_name) || "Service plan" : plans.length ? "No active plan" : ""}
                reason={fr ? "No plan in FieldRoutes" : NOT_LINKED}
              />
              <Fact
                label="Frequency"
                value={plan ? frequencyWords(plan.frequency_label || plan.frequency) : ""}
                reason={plan ? "Not set on the plan" : "No active plan"}
              />
              <Fact
                label="Price"
                value={planPrice ? money(planPrice) : ""}
                reason={plan ? "No price on the plan" : "No active plan"}
                num
              />
              <Fact label="Last service" value={lastService} reason="No completed visit on record" />
              <Fact label="Next service" value={nextService} reason="Nothing scheduled" />
              <Fact label="Tech" value={fr?.tech} reason={fr ? "No visit on record" : NOT_LINKED} />
              <Fact label="Rep" value={fr?.rep} reason={fr ? "Not set in FieldRoutes" : NOT_LINKED} />
              <Fact label="Autopay" value={fr?.autopay} reason={fr ? "Not synced yet" : NOT_LINKED} />
              <Fact
                label="Balance"
                value={balance}
                reason="No billing source"
                tone={balanceNum > 0 ? "warn" : undefined}
                num
              />
              <Fact label="Lifetime paid" value={lifetime} reason={lifetimeWhy} num />
              <Fact label="Customer since" value={since} reason="Start date not on record" />
              {fr?.linked_accounts > 1 ? (
                <Fact
                  label="FieldRoutes"
                  value={`${fr.linked_accounts} accounts linked — showing FR ${fr.customer_id}`}
                  tone="warn"
                />
              ) : null}
            </div>

            <PropertyMap address={addressQuery} known={knownCoords} height={240} expandedHeight={480} className="cov-map" />
          </section>
        </CardBoundary>
      </div>

      {/* ── Account brief: collapsed unless it has something in it ── */}
      {briefCustomerId ? (
        <section className="cov-brief">
          <AccountBriefBoundary>
            <AccountBriefCard customerId={briefCustomerId} variant="popup" collapsible />
          </AccountBriefBoundary>
        </section>
      ) : null}

      {/* ── Plans / Appointments: compact lists ── */}
      <div className="cov-lists">
        <CardBoundary name="Plans">
          <section className="cov-list" aria-label="Plans">
            <div className="cov-list__head">
              <span>Plans</span>
              <span className="cov-list__count num">{plans.length}</span>
            </div>
            {plans.length === 0 ? (
              <div className="cov-empty">{fr ? "No plans in FieldRoutes." : "No service plans. Add one from Quotes."}</div>
            ) : (
              plans.slice(0, 4).map((p, i) => {
                const active = PLAN_ACTIVE.has(clean(p.status).toLowerCase());
                const price = Number(p.recurring_charge || p.price);
                return (
                  <div key={p.id || i} className="cov-li">
                    <span className="cov-li__title">{clean(p.plan_name) || "Service plan"}</span>
                    <span className="cov-li__meta">
                      {[frequencyWords(p.frequency_label || p.frequency || p.billing_frequency), price ? money(price) : ""]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                    <span className="cov-li__date">{p.next_service ? `Next ${shortDate(p.next_service)}` : ""}</span>
                    <Badge tone={active ? "success" : "neutral"}>{plainWords(p.status) || "Unknown"}</Badge>
                  </div>
                );
              })
            )}
          </section>
        </CardBoundary>

      </div>
    </div>
  );
}
